import React, { useCallback, useMemo, useRef, useState } from 'react';

import { Box, Dialog, Stack } from '@lyearn/ui';
import { EditorMode } from '@/components/ContentEditor/types';
import Loader from '@/components/Loader';
import { DEFAULT_VALUE } from '@/components/PageLayout';
import PageLayoutContextProvider from '@/components/PageLayout/context';
import { PageLayoutType } from '@/components/PageLayout/types';
import ExerciseCreator from '@/modules/corporate/modules/exercise/views/Creator';

import { EmbeddedExerciseEditorContextType, EmbeddedExerciseEditorProvider } from './context';

const DIALOG_CLASSES = {
  paper: 'my-32 mx-24 w-full max-w-none h-full rounded-8 border border-border',
};

interface ExerciseEditorModalProps {
  open: boolean;
  exerciseId?: string;
  updateAttribute: (args: object) => void;
  onClose: () => void;
}

function ExerciseEditorModal(props: ExerciseEditorModalProps) {
  const { open, exerciseId, updateAttribute, onClose } = props;
  const [headerProps, setHeaderProps] = useState<PageLayoutType>(DEFAULT_VALUE);

  // Hold the newly-created exercise id in a ref (not state) so handleClose reads the
  // latest value synchronously. On the publish path useExerciseActions calls
  // onCreated(id) and embeddedEditor.onClose() in the same tick — a state-based read
  // in handleClose would still see the stale (null) value and skip the block write.
  // Persisting the id is deferred to close time so we don't mutate the ProseMirror
  // node (and tear down this modal) while the editor is still open.
  const createdExerciseIdRef = useRef<string | null>(null);

  const onCreated = useCallback((id: string) => {
    createdExerciseIdRef.current = id;
  }, []);

  const handleClose = useCallback(() => {
    if (createdExerciseIdRef.current) {
      updateAttribute({ exerciseId: createdExerciseIdRef.current });
    }
    createdExerciseIdRef.current = null;
    onClose();
  }, [updateAttribute, onClose]);

  const embeddedContext = useMemo<EmbeddedExerciseEditorContextType>(
    () => ({ isEmbedded: true, onClose: handleClose }),
    [handleClose],
  );

  return (
    // disableEnforceFocus so nested popovers/poppers rendered outside the dialog DOM
    // (e.g. the PublishChangesPopover shown when publishing an already-published
    // exercise) remain interactive instead of having focus trapped back into the dialog.
    <Dialog disableEnforceFocus classes={DIALOG_CLASSES} open={open} onClose={handleClose}>
      <PageLayoutContextProvider setHeaderProps={setHeaderProps}>
        <EmbeddedExerciseEditorProvider value={embeddedContext}>
          <Stack className="h-full" direction="col">
            <Stack
              alignItems="center"
              className="h-[52px] flex-shrink-0 border-b border-border bg-bg-primary px-8"
              direction="row">
              <Box className="flex flex-grow flex-row items-center">{headerProps.LeftNode}</Box>
              <Box className="flex flex-row items-center me-12">{headerProps.RightNode}</Box>
            </Stack>
            <Box className="min-h-0 flex-1 overflow-auto">
              {/*
                ExerciseCreator (and its lazily-loaded descendants) can suspend on mount.
                Without this local boundary the suspension bubbles up to the nearest
                Suspense ancestor at the course-editor level, unmounting this block's
                ProseMirror NodeView — which discards the modal's open state and snaps
                the dialog shut. Keep the boundary here so suspension is contained.
              */}
              <React.Suspense fallback={<Loader />}>
                <ExerciseCreator
                  editorMode={exerciseId ? EditorMode.Edit : EditorMode.Create}
                  exerciseId={exerciseId}
                  onCreated={onCreated}
                />
              </React.Suspense>
            </Box>
          </Stack>
        </EmbeddedExerciseEditorProvider>
      </PageLayoutContextProvider>
    </Dialog>
  );
}

export default ExerciseEditorModal;
